import { useState, useEffect, useMemo } from 'react'
import { useWorkspace } from '../context/useWorkspace'
import {
  getWorkspaceTodayIso,
  getWorkspaceWeekKey,
  isWorkspaceDayDifferentFromDevice,
  formatWorkspaceDayLabel,
  calculateMsUntilWorkspaceMidnight,
} from '../services/workspaceDayService'
import { resolveWorkspaceTimeZone } from '../services/repeat/keys'

export interface WorkspaceDayInfo {
  todayIso: string
  weekKey: string
  timeZone: string
  deviceTodayIso: string
  isDifferentFromDevice: boolean
  workspaceDayLabel: string
}

export function useWorkspaceDay(
  workspaceId?: string | null,
  overrideTimezone?: string,
): WorkspaceDayInfo {
  let contextWorkspace: { timezone?: string } | undefined
  try {
    const wsCtx = useWorkspace()
    const targetWsId = workspaceId || wsCtx.activeWorkspaceId
    contextWorkspace = wsCtx.workspaces?.find((w) => w.id === targetWsId)
  } catch {
    // Graceful fallback outside WorkspaceProvider
  }

  const timeZone = useMemo(() => {
    const raw = overrideTimezone ?? contextWorkspace?.timezone
    if (typeof raw === 'string' && raw.trim().length > 0) {
      return resolveWorkspaceTimeZone(raw)
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  }, [overrideTimezone, contextWorkspace?.timezone])

  const [tick, setTick] = useState(0)

  useEffect(() => {
    let timerId: ReturnType<typeof setTimeout> | null = null
    let disposed = false
    let iterations = 0

    const scheduleNextMidnight = () => {
      // Guard against infinite timer exhaustion loops in test runners (e.g. vi.runAllTimers)
      if (iterations++ >= 5) return
      const ms = calculateMsUntilWorkspaceMidnight(timeZone)
      // Cap max timeout to 24 hours (or at least 500ms)
      const delay = Math.max(500, Math.min(ms + 250, 86_400_000))
      timerId = setTimeout(() => {
        if (!disposed) {
          setTick((t) => t + 1)
          scheduleNextMidnight()
        }
      }, delay)
    }

    scheduleNextMidnight()

    return () => {
      disposed = true
      if (timerId) clearTimeout(timerId)
    }
  }, [timeZone])

  return useMemo(() => {
    void tick
    const now = new Date()
    const todayIso = getWorkspaceTodayIso(timeZone, now)
    const weekKey = getWorkspaceWeekKey(timeZone, now)
    const devTz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const deviceTodayIso = getWorkspaceTodayIso(devTz, now)
    const isDifferent = isWorkspaceDayDifferentFromDevice(timeZone, devTz, now)
    const workspaceDayLabel = formatWorkspaceDayLabel(timeZone, now)

    return {
      todayIso,
      weekKey,
      timeZone,
      deviceTodayIso,
      isDifferentFromDevice: isDifferent,
      workspaceDayLabel,
    }
  }, [timeZone, tick])
}
